import React from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined';
import { Box, Button, Typography } from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  LIABILITY_TABLE_COLUMN_WIDTHS,
  LIABILITY_TABLE_HEADERS,
  LIABILITY_TABLE_MIN_WIDTH,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/liabilities/constants.ts';
import { LiabilityRow } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/liabilities/LiabilityRow.tsx';
import type { ILiabilityGroupProps } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/liabilities/types.ts';
import { FinancialConditionTable } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/shared/FinancialConditionTable.tsx';
import { financialConditionGenerationStyles } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/styles.ts';
import type { TFinancialConditionFormValues } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import { REQUIRED_LIABILITY_REPORT_LINE_CODES } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/constants.ts';
import { createLiability } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/utils.ts';
import { documentGenerationFormStyles } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/styles.ts';

export const LiabilityGroup: React.FC<ILiabilityGroupProps> = ({
  name,
  title,
  disabled,
  creditorOptions,
  isBanksLoading,
}) => {
  const { control } = useFormContext<TFinancialConditionFormValues>();
  const { fields, append, remove } = useFieldArray({ control, name });
  const canRemoveLiability = (reportLine: string) =>
    fields.length > 1 &&
    !REQUIRED_LIABILITY_REPORT_LINE_CODES.includes(
      reportLine.replace(/\D/g, '') as (typeof REQUIRED_LIABILITY_REPORT_LINE_CODES)[number]
    );
  const showActions = fields.some((field) => canRemoveLiability(field.reportLine));

  return (
    <Box sx={financialConditionGenerationStyles.financialAssistanceGroup}>
      <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
      <FinancialConditionTable
        ariaLabel={title}
        headers={[...LIABILITY_TABLE_HEADERS]}
        showActions={showActions}
        dateColumnIndexes={[4, 5]}
        columnWidths={[...LIABILITY_TABLE_COLUMN_WIDTHS]}
        minWidth={LIABILITY_TABLE_MIN_WIDTH}
      >
        {fields.map((field, index) => (
          <LiabilityRow
            key={field.id}
            name={name}
            index={index}
            canRemove={canRemoveLiability(field.reportLine)}
            disabled={disabled}
            creditorOptions={creditorOptions}
            isBanksLoading={isBanksLoading}
            onRemove={() => remove(index)}
          />
        ))}
      </FinancialConditionTable>
      <Button
        variant="outlined"
        startIcon={<AddCircleOutlineIcon />}
        onClick={() => append(createLiability())}
        disabled={disabled}
        sx={documentGenerationFormStyles.addButton}
      >
        Додати зобов’язання
      </Button>
    </Box>
  );
};
